import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, X, Plus, MoreVertical, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Popover as PopoverRoot, PopoverTrigger as PopoverTriggerRoot, PopoverContent as PopoverContentRoot,
} from '../../../../components/ui/popover';
import {
  Command as CommandRoot, CommandInput as CommandInputRoot, CommandList as CommandListRoot,
  CommandEmpty as CommandEmptyRoot, CommandItem as CommandItemRoot,
} from '../../../../components/ui/command';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../../../components/ui/alert-dialog';

// shadcn .jsx senza tipi: cast esplicito
const Popover = PopoverRoot as React.FC<{ open?: boolean; onOpenChange?: (o: boolean) => void; children?: React.ReactNode }>;
const PopoverTrigger = PopoverTriggerRoot as React.FC<{ asChild?: boolean; children?: React.ReactNode }>;
const PopoverContent = PopoverContentRoot as React.FC<{ className?: string; align?: 'start' | 'center' | 'end'; sideOffset?: number; children?: React.ReactNode }>;
const Command = CommandRoot as React.FC<{ className?: string; shouldFilter?: boolean; children?: React.ReactNode }>;
const CommandInput = CommandInputRoot as React.FC<{ placeholder?: string; value?: string; onValueChange?: (v: string) => void; autoFocus?: boolean }>;
const CommandList = CommandListRoot as React.FC<{ className?: string; children?: React.ReactNode }>;
const CommandEmpty = CommandEmptyRoot as React.FC<{ children?: React.ReactNode }>;
const CommandItem = CommandItemRoot as React.FC<{ value?: string; onSelect?: () => void; className?: string; children?: React.ReactNode }>;

export interface AssocItem {
  /** ID interno usato per le operazioni (machineId per articoli, sku per macchine). */
  id: string;
  /** Codice mostrato in colonna CODICE (può essere abbreviato). */
  code: string;
  brand: string;
  primary: string;
  secondary?: string;
}

export interface AddSearchResult {
  /** ObjectId macchina o SKU articolo */
  id: string;
  /** Modello o descrizione */
  primary: string;
  /** Marca */
  secondary: string;
  /** Tipo / categoria */
  meta?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle: string;
  codeColumnLabel?: string;
  primaryColumnLabel: string;
  items: AssocItem[];
  loading?: boolean;
  filterPlaceholder?: string;
  testIdPrefix: string;
  /** Se passato, abilita il bottone "+ Nuovo" con Command per cercare item da associare. */
  onAddSearch?: (query: string) => Promise<AddSearchResult[]>;
  /** Chiamato dopo che l'utente sceglie un item dal Command. Deve effettuare la creazione lato server. */
  onAddSelect?: (item: AddSearchResult) => Promise<void>;
  /** Placeholder ricerca per il Command "+ Nuovo". */
  addSearchPlaceholder?: string;
  /** Pannello aggiuntivo renderizzato sotto la lista (sopra la barra di selezione). */
  extraPanel?: React.ReactNode;
  onRemove?: (codes: string[]) => Promise<void>;
  /** Chiamato dopo ogni add/remove riuscito per ricaricare la lista. */
  onRefresh?: () => void;
}

export default function AssociationsDialog({
  open, onClose, title, subtitle,
  codeColumnLabel, primaryColumnLabel,
  items, loading,
  filterPlaceholder = 'cerca per marca, codice o descrizione…',
  testIdPrefix,
  onAddSearch, onAddSelect, addSearchPlaceholder = 'cerca…',
  onRemove, onRefresh,
  extraPanel,
}: Props) {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // "+ Nuovo" popover
  const [addOpen, setAddOpen] = useState(false);
  const [addQuery, setAddQuery] = useState('');
  const [addResults, setAddResults] = useState<AddSearchResult[]>([]);
  const [addLoading, setAddLoading] = useState(false);
  const [addBusy, setAddBusy] = useState(false);
  const addDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Remove confirmation
  const [confirmCodes, setConfirmCodes] = useState<string[] | null>(null);
  const [removing, setRemoving] = useState(false);

  // Per-row popover state (sola anchor)
  const [rowMenuCode, setRowMenuCode] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setQuery(''); setSelected(new Set());
      setAddOpen(false); setAddQuery(''); setAddResults([]);
      setConfirmCodes(null); setRowMenuCode(null);
    }
  }, [open]);

  // Live search per "+ Nuovo"
  useEffect(() => {
    if (!addOpen || !onAddSearch) return;
    if (addDebounceRef.current) clearTimeout(addDebounceRef.current);
    if (!addQuery || addQuery.length < 2) { setAddResults([]); return; }
    setAddLoading(true);
    addDebounceRef.current = setTimeout(() => {
      onAddSearch(addQuery)
        .then(r => setAddResults(r))
        .catch(() => setAddResults([]))
        .finally(() => setAddLoading(false));
    }, 250);
    return () => { if (addDebounceRef.current) clearTimeout(addDebounceRef.current); };
  }, [addQuery, addOpen, onAddSearch]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(it =>
      (it.brand || '').toLowerCase().includes(q) ||
      (it.code || '').toLowerCase().includes(q) ||
      (it.primary || '').toLowerCase().includes(q),
    );
  }, [items, query]);

  const allFilteredSelected = filtered.length > 0 &&
    filtered.every(it => selected.has(it.id));

  if (!open) return null;

  const toggleOne = (id: string) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };
  const toggleAll = () => {
    const s = new Set(selected);
    filtered.forEach(it => allFilteredSelected ? s.delete(it.id) : s.add(it.id));
    setSelected(s);
  };

  const handleAddSelect = async (item: AddSearchResult) => {
    if (!onAddSelect) return;
    setAddBusy(true);
    try {
      await onAddSelect(item);
      toast.success(`Associazione creata: ${item.primary}`);
      setAddQuery(''); setAddResults([]); setAddOpen(false);
      onRefresh?.();
    } catch (e: any) {
      toast.error('Errore: ' + (e?.response?.data?.detail ?? e?.message ?? 'unknown'));
    } finally {
      setAddBusy(false);
    }
  };

  const handleRemove = async () => {
    if (!confirmCodes || !onRemove) return;
    setRemoving(true);
    try {
      await onRemove(confirmCodes);
      toast.success(`${confirmCodes.length} ${confirmCodes.length === 1 ? 'associazione rimossa' : 'associazioni rimosse'}`);
      setConfirmCodes(null); setSelected(new Set());
      onRefresh?.();
    } catch (e: any) {
      toast.error('Errore: ' + (e?.response?.data?.detail ?? e?.message ?? 'unknown'));
    } finally {
      setRemoving(false);
    }
  };

  const showSelectionColumn = !!onRemove;
  const showActionColumn = !!onRemove;

  // Colonne dinamiche
  const gridCols = [
    showSelectionColumn ? '34px' : null,
    codeColumnLabel ? '110px' : null,
    '1fr',
    '110px',
    showActionColumn ? '36px' : null,
  ].filter(Boolean).join(' ');

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      data-testid={`${testIdPrefix}-dialog`}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-[11px] shadow-2xl max-w-3xl w-full h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex-none px-6 py-4 border-b border-[#eee]">
          <div className="flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-[17px] font-semibold text-[#1f1f1d] truncate" data-testid={`${testIdPrefix}-title`}>
                {title}
              </div>
              <div className="text-[13px] font-mono text-[#5c5c57] mt-0.5">
                {subtitle} · {items.length} totali
                {query && filtered.length !== items.length ? ` · ${filtered.length} filtrati` : ''}
              </div>
            </div>
            <button
              onClick={onClose}
              data-testid={`${testIdPrefix}-close`}
              className="text-[#5c5c57] hover:text-[#5c5c57] flex-none"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Filter + Nuovo */}
          <div className="mt-3 flex items-center gap-2">
            <div className="flex-1 flex items-center gap-2 px-2.5 py-[7px] border border-[#d8d8d4] rounded-[7px] bg-[#fafafa]">
              <Search className="w-3.5 h-3.5 text-[#5c5c57] flex-none" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={filterPlaceholder}
                data-testid={`${testIdPrefix}-filter`}
                className="flex-1 text-[14px] outline-none bg-transparent placeholder:text-[#6d6d68]"
              />
              {query && (
                <button onClick={() => setQuery('')} className="text-[#5c5c57] hover:text-[#5c5c57]" data-testid={`${testIdPrefix}-filter-clear`}>
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>

            {onAddSearch && onAddSelect && (
              <Popover open={addOpen} onOpenChange={setAddOpen}>
                <PopoverTrigger asChild>
                  <button
                    data-testid={`${testIdPrefix}-add-btn`}
                    className="px-3 py-2 rounded-[7px] bg-[#2b3a8f] text-white text-[14px] font-semibold hover:bg-[#243279] flex items-center gap-1.5 flex-none"
                  >
                    <Plus className="w-3.5 h-3.5" /> Nuovo
                  </button>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-[420px]" align="end" sideOffset={6}>
                  <Command shouldFilter={false}>
                    <CommandInput
                      autoFocus
                      placeholder={addSearchPlaceholder}
                      value={addQuery}
                      onValueChange={setAddQuery}
                    />
                    <CommandList className="max-h-[300px]">
                      {addQuery.length < 2 ? (
                        <CommandEmpty>Digita almeno 2 caratteri.</CommandEmpty>
                      ) : addLoading ? (
                        <CommandEmpty>
                          <span className="inline-flex items-center gap-1.5">
                            <Loader2 className="w-3 h-3 animate-spin" /> Ricerca…
                          </span>
                        </CommandEmpty>
                      ) : addResults.length === 0 ? (
                        <CommandEmpty>Nessun risultato.</CommandEmpty>
                      ) : (
                        addResults.map(r => (
                          <CommandItem
                            key={r.id}
                            value={`${r.id}-${r.primary}`}
                            onSelect={() => !addBusy && handleAddSelect(r)}
                          >
                            <div
                              data-testid={`${testIdPrefix}-add-option-${r.id}`}
                              className="flex flex-col w-full min-w-0"
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="font-mono text-[13px] text-[#2b3a8f] font-semibold flex-none">{r.id.slice(-10)}</span>
                                <span className="flex-1 truncate text-[14px] text-[#1f1f1d]">{r.primary}</span>
                                <span className="text-[12px] font-mono text-[#5c5c57] flex-none">{r.secondary}</span>
                              </div>
                              {r.meta && <div className="text-[12px] text-[#5c5c57] truncate">{r.meta}</div>}
                            </div>
                          </CommandItem>
                        ))
                      )}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            )}
          </div>
        </div>

        {/* Column header */}
        <div
          className="flex-none px-6 py-2 bg-[#fafafa] border-b border-[#e4e4e1] grid gap-3 items-center"
          style={{ gridTemplateColumns: gridCols }}
        >
          {showSelectionColumn && (
            <div className="text-center">
              <input
                type="checkbox"
                checked={allFilteredSelected}
                onChange={toggleAll}
                disabled={filtered.length === 0}
                data-testid={`${testIdPrefix}-select-all`}
              />
            </div>
          )}
          {codeColumnLabel && (
            <div className="text-[11px] font-bold tracking-[0.08em] text-[#5c5c57] font-mono">{codeColumnLabel}</div>
          )}
          <div className="text-[11px] font-bold tracking-[0.08em] text-[#5c5c57] font-mono">{primaryColumnLabel}</div>
          <div className="text-[11px] font-bold tracking-[0.08em] text-[#5c5c57] font-mono">MARCA</div>
          {showActionColumn && <div />}
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="px-6 py-10 text-center text-[14px] text-[#5c5c57]">Caricamento…</div>
          ) : items.length === 0 ? (
            <div className="px-6 py-10 text-center text-[14px] text-[#5c5c57]" data-testid={`${testIdPrefix}-empty`}>
              Nessuna associazione presente.
            </div>
          ) : filtered.length === 0 ? (
            <div className="px-6 py-10 text-center text-[14px] text-[#5c5c57]" data-testid={`${testIdPrefix}-no-results`}>
              Nessun risultato per "<span className="font-mono">{query}</span>"
            </div>
          ) : (
            filtered.map((it, i) => {
              const isSel = selected.has(it.id);
              return (
                <div
                  key={`${it.id}-${i}`}
                  data-testid={`${testIdPrefix}-row-${it.id}`}
                  className={`px-6 py-2.5 border-b border-[#f1f1ee] grid gap-3 items-center ${isSel ? 'bg-[#fafbff]' : 'hover:bg-[#fafafa]'}`}
                  style={{ gridTemplateColumns: gridCols }}
                >
                  {showSelectionColumn && (
                    <div className="text-center">
                      <input
                        type="checkbox"
                        checked={isSel}
                        onChange={() => toggleOne(it.id)}
                        data-testid={`${testIdPrefix}-row-check-${it.id}`}
                      />
                    </div>
                  )}
                  {codeColumnLabel && (
                    <div className="text-[14px] font-mono font-semibold text-[#1f1f1d] truncate">{it.code}</div>
                  )}
                  <div className="text-[14px] text-[#3a3a36] min-w-0">
                    <div className="truncate" title={it.primary}>{it.primary}</div>
                    {it.secondary && (
                      <div className="text-[12px] font-mono text-[#5c5c57] truncate">{it.secondary}</div>
                    )}
                  </div>
                  <div className="text-[14px] text-[#5c5c57] truncate">{it.brand || '—'}</div>
                  {showActionColumn && (
                    <div className="text-center">
                      <Popover
                        open={rowMenuCode === it.id}
                        onOpenChange={(o) => setRowMenuCode(o ? it.id : null)}
                      >
                        <PopoverTrigger asChild>
                          <button
                            data-testid={`${testIdPrefix}-row-menu-${it.id}`}
                            className="text-[#5c5c57] hover:text-[#3a3a36] p-1 rounded hover:bg-[#f3f3f0]"
                          >
                            <MoreVertical className="w-3.5 h-3.5" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="p-1 w-44" align="end" sideOffset={4}>
                          <button
                            data-testid={`${testIdPrefix}-row-remove-${it.id}`}
                            onClick={() => { setRowMenuCode(null); setConfirmCodes([it.id]); }}
                            className="w-full flex items-center gap-2 px-2.5 py-1.5 text-[14px] text-[#8a3b3b] hover:bg-[#fdf5f5] rounded text-left"
                          >
                            <Trash2 className="w-3.5 h-3.5" /> Rimuovi associazione
                          </button>
                        </PopoverContent>
                      </Popover>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Footer selection bar */}
        {extraPanel}
        {showSelectionColumn && selected.size > 0 && (
          <div
            className="flex-none px-6 py-3 border-t border-[#eee] bg-[#fafbff] flex items-center gap-3"
            data-testid={`${testIdPrefix}-bulk-bar`}
          >
            <span className="text-[14px] text-[#3a3a36]">
              <strong className="text-[#2b3a8f]">{selected.size}</strong> selezionate
            </span>
            <button
              onClick={() => setSelected(new Set())}
              className="text-[13px] text-[#5c5c57] hover:underline"
              data-testid={`${testIdPrefix}-bulk-clear`}
            >
              annulla selezione
            </button>
            <div className="flex-1" />
            <button
              onClick={() => setConfirmCodes(Array.from(selected))}
              data-testid={`${testIdPrefix}-bulk-remove`}
              className="px-3 py-1.5 rounded-[7px] bg-[#8a3b3b] text-white text-[14px] font-semibold hover:bg-[#722f2f] flex items-center gap-1.5"
            >
              <Trash2 className="w-3.5 h-3.5" /> Rimuovi {selected.size}
            </button>
          </div>
        )}
      </div>

      {/* Confirm removal */}
      <AlertDialog
        open={confirmCodes !== null}
        onOpenChange={(o) => { if (!o && !removing) setConfirmCodes(null); }}
      >
        <AlertDialogContent data-testid={`${testIdPrefix}-remove-confirm`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Confermi la rimozione?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmCodes && (
                <>
                  Stai per rimuovere <strong>{confirmCodes.length}</strong> {confirmCodes.length === 1 ? 'associazione' : 'associazioni'}.
                  L'azione è immediata e non è reversibile dall'interfaccia (puoi ricrearle dal bottone "+ Nuovo").
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removing} data-testid={`${testIdPrefix}-remove-cancel`}>
              Annulla
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemove}
              disabled={removing}
              data-testid={`${testIdPrefix}-remove-confirm-yes`}
              className="bg-[#8a3b3b] hover:bg-[#722f2f]"
            >
              {removing ? 'Rimozione…' : 'Sì, rimuovi'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
